import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
/** No cycle to report — a month with no goals, or a scope that resolves to nothing. */
export const EMPTY_BILLING_CARDS = {
    total_billing: 0,
    total_billable_clients: 0,
    total_amount_billed: 0,
    total_amount_collected: 0,
    clients_billed: 0,
    clients_collected: 0,
    amount_remaining_to_collect: 0,
    clients_remaining_to_collect: 0,
};
const MODULE_KEY = "billing_summary";
// ==========================================================================
// ✅ ASYNC THUNK
// ==========================================================================
export const fetchBillingSummary = createAsyncThunk("billingSummary/fetch", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("GET", "/api/billing-summary", payload, {
            Authorization: token || "",
            "x-module-key": MODULE_KEY,
        });
        const errLike = response;
        const status = errLike?.response?.status ?? errLike?.status;
        const serverMessage = errLike?.response?.data?.message ?? errLike?.data?.message;
        if (!response || errLike?.isAxiosError || (typeof status === "number" && status >= 400)) {
            return rejectWithValue(serverMessage || errLike?.message || "An error occurred while fetching the billing summary");
        }
        if (response && "data" in response && response.data) {
            const body = response.data;
            // `getbillingSummaryMeta` returns null when the month has no goals or
            // the user's client scope resolves to nothing — treat that as empty,
            // not as an error, so the table shows its own empty state.
            const result = body?.data;
            return {
                summary: Array.isArray(result?.summary) ? result.summary : [],
                cards: result?.cards ?? EMPTY_BILLING_CARDS,
                last_updated: result?.last_updated ?? null,
            };
        }
        return rejectWithValue("Unexpected empty response while fetching the billing summary");
    }
    catch (error) {
        console.error("Billing Summary API Error:", error);
        return rejectWithValue(error?.response?.data?.message || error?.message || "An error occurred while fetching the billing summary");
    }
});
/**
 * Add / update one client's development hours for the month.
 *
 * The listing carries the saved lines back on each row, so nothing is written into
 * this slice on success — the page re-fetches the summary instead, which also picks up
 * the new Total Bill and the footer/KPI figures the lines feed into.
 */
export const saveDevelopmentHours = createAsyncThunk("billingSummary/saveDevelopmentHours", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/billing-summary/development-hours", payload, {
            Authorization: token || "",
            "x-module-key": MODULE_KEY,
        });
        const errLike = response;
        const status = errLike?.response?.status ?? errLike?.status;
        const serverMessage = errLike?.response?.data?.message ?? errLike?.data?.message;
        if (!response || errLike?.isAxiosError || (typeof status === "number" && status >= 400)) {
            return rejectWithValue(serverMessage || errLike?.message || "An error occurred while saving the development hours");
        }
        const body = response?.data;
        if (body?.success === false) {
            return rejectWithValue(body?.message || "An error occurred while saving the development hours");
        }
        return (body?.data ?? {
            ...payload,
            development_hours: [],
            development_hours_total: 0,
        });
    }
    catch (error) {
        console.error("Save Development Hours API Error:", error);
        return rejectWithValue(error?.response?.data?.message || error?.message || "An error occurred while saving the development hours");
    }
});
/**
 * Add / update the commission entered for a Tab-sourced client's month.
 *
 * Like the development-hours save, nothing is written into this slice on success: the
 * amount replaces the row's Commissions and moves the Total Bill, the footer totals and
 * the KPI cards, so the page re-fetches the listing instead of patching the row.
 */
export const saveCommissionAmount = createAsyncThunk("billingSummary/saveCommissionAmount", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/billing-summary/commission-amount", payload, {
            Authorization: token || "",
            "x-module-key": MODULE_KEY,
        });
        const errLike = response;
        const status = errLike?.response?.status ?? errLike?.status;
        const serverMessage = errLike?.response?.data?.message ?? errLike?.data?.message;
        if (!response || errLike?.isAxiosError || (typeof status === "number" && status >= 400)) {
            return rejectWithValue(serverMessage || errLike?.message || "An error occurred while saving the commission details");
        }
        const body = response?.data;
        if (body?.success === false) {
            return rejectWithValue(body?.message || "An error occurred while saving the commission details");
        }
        return (body?.data ?? {
            client_id: payload.client_id,
            year_month: payload.year_month,
            commission_amount: null,
            sales_description: null,
        });
    }
    catch (error) {
        console.error("Save Commission Amount API Error:", error);
        return rejectWithValue(error?.response?.data?.message || error?.message || "An error occurred while saving the commission details");
    }
});
/**
 * Override the Total Bill for one or more clients in the month.
 *
 * Like the development-hours save, nothing is written into this slice on success: an
 * override moves Total Bill, the footer totals and the KPI cards, so the page re-fetches
 * the listing instead of patching the affected rows.
 */
export const saveAdjustedAmounts = createAsyncThunk("billingSummary/saveAdjustedAmounts", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/billing-summary/adjusted-amount", payload, {
            Authorization: token || "",
            "x-module-key": MODULE_KEY,
        });
        const errLike = response;
        const status = errLike?.response?.status ?? errLike?.status;
        const serverMessage = errLike?.response?.data?.message ?? errLike?.data?.message;
        if (!response || errLike?.isAxiosError || (typeof status === "number" && status >= 400)) {
            return rejectWithValue(serverMessage || errLike?.message || "An error occurred while updating the Total Bill");
        }
        const body = response?.data;
        if (body?.success === false) {
            return rejectWithValue(body?.message || "An error occurred while updating the Total Bill");
        }
        return (body?.data ?? { year_month: payload.year_month, saved: 0 });
    }
    catch (error) {
        console.error("Save Adjusted Amounts API Error:", error);
        return rejectWithValue(error?.response?.data?.message || error?.message || "An error occurred while updating the Total Bill");
    }
});
/**
 * Queue QuickBooks invoices for the checked clients.
 *
 * A client already holding an invoice for the month is UPDATED in place, so re-running
 * after a correction revises it rather than raising a duplicate.
 *
 * Only starts the work — see `GenerateInvoiceResult`. Does NOT reject when a generation is
 * already running: that comes back as `started: false` with the server's message, since
 * waiting for the one in flight is the right thing to do.
 */
export const generateInvoice = createAsyncThunk("billingSummary/generateInvoice", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/billing-summary/generate-invoice", payload, {
            Authorization: token || "",
            "x-module-key": MODULE_KEY,
        });
        const errLike = response;
        const status = errLike?.response?.status ?? errLike?.status;
        const serverMessage = errLike?.response?.data?.message ?? errLike?.data?.message;
        if (!response || errLike?.isAxiosError || (typeof status === "number" && status >= 400)) {
            return rejectWithValue(serverMessage || errLike?.message || "An error occurred while generating the invoice(s)");
        }
        const body = response?.data;
        return {
            // `success: false` here means a generation was already in flight — benign, so
            // it resolves with the flag rather than rejecting.
            started: body?.success !== false,
            message: body?.message || "",
            queued: Number(body?.data?.queued ?? 0),
        };
    }
    catch (error) {
        console.error("Generate Invoice API Error:", error);
        return rejectWithValue(error?.response?.data?.message || error?.message || "An error occurred while generating the invoice(s)");
    }
});
/**
 * Reconcile the month against QuickBooks and queue its invoices for a status refresh.
 *
 * The actual re-reading is done by a background worker, so this only starts the job —
 * poll `fetchRefreshStatus` to learn when it's finished.
 *
 * Note this does NOT reject on `success: false`. The endpoint answers 200 with that flag
 * for two benign outcomes — a refresh already in flight, and a month with nothing to
 * refresh — so both come back as `started: false` plus the server's own message.
 */
export const refreshInvoices = createAsyncThunk("billingSummary/refreshInvoices", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/billing-summary/refresh-invoices", payload, {
            Authorization: token || "",
            "x-module-key": MODULE_KEY,
        });
        const errLike = response;
        const status = errLike?.response?.status ?? errLike?.status;
        const serverMessage = errLike?.response?.data?.message ?? errLike?.data?.message;
        if (!response || errLike?.isAxiosError || (typeof status === "number" && status >= 400)) {
            return rejectWithValue(serverMessage || errLike?.message || "An error occurred while starting the invoice refresh");
        }
        const body = response?.data;
        const outcome = body?.data ?? {};
        return {
            started: body?.success !== false,
            message: body?.message || "",
            adopted: Number(outcome.adopted ?? 0),
            zeroed: Number(outcome.zeroed ?? 0),
            queued: Number(outcome.queued ?? 0),
            multiple: Array.isArray(outcome.multiple) ? outcome.multiple : [],
            flagged: Number(outcome.flagged ?? 0),
        };
    }
    catch (error) {
        console.error("Refresh Invoices API Error:", error);
        return rejectWithValue(error?.response?.data?.message || error?.message || "An error occurred while starting the invoice refresh");
    }
});
/**
 * Whether the refresh worker is still going — polled by the page after a refresh starts.
 *
 * The running flag comes from `data.running`, NOT from `success`: this endpoint sets
 * `success` to "not running", so reading it would report a refresh in progress as a
 * failed request.
 */
export const fetchRefreshStatus = createAsyncThunk("billingSummary/fetchRefreshStatus", async (_arg, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("GET", "/api/billing-summary/refresh-status", {}, {
            Authorization: token || "",
            "x-module-key": MODULE_KEY,
        });
        const errLike = response;
        const status = errLike?.response?.status ?? errLike?.status;
        const serverMessage = errLike?.response?.data?.message ?? errLike?.data?.message;
        if (!response || errLike?.isAxiosError || (typeof status === "number" && status >= 400)) {
            return rejectWithValue(serverMessage || errLike?.message || "An error occurred while checking the refresh status");
        }
        const body = response?.data;
        return {
            running: Boolean(body?.data?.running),
            refreshing: Boolean(body?.data?.refreshing),
            generating: Boolean(body?.data?.generating),
            message: body?.message || "",
        };
    }
    catch (error) {
        console.error("Refresh Status API Error:", error);
        return rejectWithValue(error?.response?.data?.message || error?.message || "An error occurred while checking the refresh status");
    }
});
/**
 * The saved internal review state for one invoice, read as the preview modal opens.
 *
 * Deliberately not stored in the slice: it's scoped to whichever invoice is open, so the
 * modal holds it as local form state.
 */
export const fetchInvoicePreview = createAsyncThunk("billingSummary/fetchInvoicePreview", async (invoiceId, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("GET", `/api/billing-summary/invoice-preview/${encodeURIComponent(invoiceId)}`, {}, {
            Authorization: token || "",
            "x-module-key": MODULE_KEY,
        });
        const errLike = response;
        const status = errLike?.response?.status ?? errLike?.status;
        const serverMessage = errLike?.response?.data?.message ?? errLike?.data?.message;
        if (!response || errLike?.isAxiosError || (typeof status === "number" && status >= 400)) {
            return rejectWithValue(serverMessage || errLike?.message || "An error occurred while fetching the invoice preview");
        }
        const body = response?.data;
        if (body?.success === false) {
            return rejectWithValue(body?.message || "An error occurred while fetching the invoice preview");
        }
        const result = body?.data ?? {};
        return {
            invoice_id: String(result.invoice_id ?? invoiceId),
            pdf_url: String(result.pdf_url ?? ""),
            approval_status: result.approval_status ?? null,
            invoice_notes: result.invoice_notes ?? null,
        };
    }
    catch (error) {
        console.error("Invoice Preview API Error:", error);
        return rejectWithValue(error?.response?.data?.message || error?.message || "An error occurred while fetching the invoice preview");
    }
});
/**
 * Store the internal approval state and notes against an invoice.
 *
 * Nothing here feeds the listing — `approval_status` / `invoice_notes` aren't columns —
 * so the page does NOT re-fetch afterwards, unlike the other saves in this slice.
 */
export const saveInvoiceNotes = createAsyncThunk("billingSummary/saveInvoiceNotes", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/billing-summary/invoice-notes", payload, {
            Authorization: token || "",
            "x-module-key": MODULE_KEY,
        });
        const errLike = response;
        const status = errLike?.response?.status ?? errLike?.status;
        const serverMessage = errLike?.response?.data?.message ?? errLike?.data?.message;
        if (!response || errLike?.isAxiosError || (typeof status === "number" && status >= 400)) {
            return rejectWithValue(serverMessage || errLike?.message || "An error occurred while saving the internal notes");
        }
        const body = response?.data;
        if (body?.success === false) {
            return rejectWithValue(body?.message || "An error occurred while saving the internal notes");
        }
        const result = body?.data ?? {};
        return {
            invoice_id: String(result.invoice_id ?? payload.invoice_id),
            approval_status: result.approval_status ?? null,
            invoice_notes: result.invoice_notes ?? null,
        };
    }
    catch (error) {
        console.error("Save Invoice Notes API Error:", error);
        return rejectWithValue(error?.response?.data?.message || error?.message || "An error occurred while saving the internal notes");
    }
});
/**
 * The additional QuickBooks invoices the checked clients hold for the billing month.
 *
 * Not stored in the slice: the list is scoped to whichever rows are checked when the modal
 * opens, so the modal holds it as local state — the same treatment `fetchInvoicePreview`
 * gets. This reaches QuickBooks, so it's slower than the module's other reads and the
 * modal shows its own loader while it runs.
 */
export const fetchMappableInvoices = createAsyncThunk("billingSummary/fetchMappableInvoices", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("GET", "/api/billing-summary/mapped-invoices", {
            year_month: payload.year_month,
            client_ids: payload.client_ids.join(","),
        }, {
            Authorization: token || "",
            "x-module-key": MODULE_KEY,
        }, 
        // Reads a whole month of invoices out of QuickBooks, which the listing's
        // own endpoints never do — the default timeout is too tight for it.
        2 * 60 * 1000);
        const errLike = response;
        const status = errLike?.response?.status ?? errLike?.status;
        const serverMessage = errLike?.response?.data?.message ?? errLike?.data?.message;
        if (!response || errLike?.isAxiosError || (typeof status === "number" && status >= 400)) {
            return rejectWithValue(serverMessage || errLike?.message || "An error occurred while fetching the invoices");
        }
        const body = response?.data;
        if (body?.success === false) {
            return rejectWithValue(body?.message || "An error occurred while fetching the invoices");
        }
        const invoices = body?.data?.invoices;
        return (Array.isArray(invoices) ? invoices : []);
    }
    catch (error) {
        console.error("Mappable Invoices API Error:", error);
        return rejectWithValue(error?.response?.data?.message || error?.message || "An error occurred while fetching the invoices");
    }
});
/**
 * Store which extra invoices are mapped onto each checked client's month.
 *
 * The payload carries each client's complete selection, so this both ADDS and REMOVES
 * mappings — see `SaveInvoiceMappingsPayload`. Nothing is written into the slice: mapping
 * doesn't move any listing column, so the page has nothing to re-read on success.
 */
export const saveInvoiceMappings = createAsyncThunk("billingSummary/saveInvoiceMappings", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/billing-summary/mapped-invoices", payload, {
            Authorization: token || "",
            "x-module-key": MODULE_KEY,
        }, 
        // Every selected invoice is re-read from QuickBooks before it's stored.
        2 * 60 * 1000);
        const errLike = response;
        const status = errLike?.response?.status ?? errLike?.status;
        const serverMessage = errLike?.response?.data?.message ?? errLike?.data?.message;
        if (!response || errLike?.isAxiosError || (typeof status === "number" && status >= 400)) {
            return rejectWithValue(serverMessage || errLike?.message || "An error occurred while saving the invoice mapping");
        }
        const body = response?.data;
        if (body?.success === false) {
            return rejectWithValue(body?.message || "An error occurred while saving the invoice mapping");
        }
        const result = body?.data ?? {};
        return {
            year_month: String(result.year_month ?? payload.year_month),
            saved: Number(result.saved ?? 0),
            failed: Array.isArray(result.failed) ? result.failed : [],
        };
    }
    catch (error) {
        console.error("Save Invoice Mappings API Error:", error);
        return rejectWithValue(error?.response?.data?.message || error?.message || "An error occurred while saving the invoice mapping");
    }
});
// ==========================================================================
// ✅ SLICE
// ==========================================================================
const initialState = {
    summary: null,
    loading: false,
    error: null,
    savingDevelopmentHours: false,
    savingCommissionAmount: false,
    savingAdjustedAmounts: false,
    generatingInvoice: false,
    savingInvoiceNotes: false,
    savingInvoiceMappings: false,
};
const billingSummarySlice = createSlice({
    name: "billingSummary",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchBillingSummary.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchBillingSummary.fulfilled, (state, action) => {
            state.loading = false;
            state.summary = action.payload;
            state.error = null;
        })
            .addCase(fetchBillingSummary.rejected, (state, action) => {
            state.loading = false;
            state.summary = null;
            state.error = action.payload || "Failed to fetch the billing summary";
        })
            // The save deliberately leaves `summary` alone — the page re-fetches, so the
            // row, the footer and the KPI cards all move together.
            .addCase(saveDevelopmentHours.pending, (state) => {
            state.savingDevelopmentHours = true;
        })
            .addCase(saveDevelopmentHours.fulfilled, (state) => {
            state.savingDevelopmentHours = false;
        })
            .addCase(saveDevelopmentHours.rejected, (state) => {
            state.savingDevelopmentHours = false;
        })
            // Same as above: the page re-fetches, since a Tab row's commission moves its
            // Total Bill and the footer / KPI figures with it.
            .addCase(saveCommissionAmount.pending, (state) => {
            state.savingCommissionAmount = true;
        })
            .addCase(saveCommissionAmount.fulfilled, (state) => {
            state.savingCommissionAmount = false;
        })
            .addCase(saveCommissionAmount.rejected, (state) => {
            state.savingCommissionAmount = false;
        })
            // Same as above — the page re-fetches, so `summary` is left alone here.
            .addCase(saveAdjustedAmounts.pending, (state) => {
            state.savingAdjustedAmounts = true;
        })
            .addCase(saveAdjustedAmounts.fulfilled, (state) => {
            state.savingAdjustedAmounts = false;
        })
            .addCase(saveAdjustedAmounts.rejected, (state) => {
            state.savingAdjustedAmounts = false;
        })
            // Invoicing rewrites invoice no / status / amount, so the page re-fetches here
            // too rather than patching the affected rows.
            .addCase(generateInvoice.pending, (state) => {
            state.generatingInvoice = true;
        })
            .addCase(generateInvoice.fulfilled, (state) => {
            state.generatingInvoice = false;
        })
            .addCase(generateInvoice.rejected, (state) => {
            state.generatingInvoice = false;
        })
            // `fetchInvoicePreview` holds no flag — the modal tracks its own load, since it
            // shows the PDF and the review state as one unit.
            .addCase(saveInvoiceNotes.pending, (state) => {
            state.savingInvoiceNotes = true;
        })
            .addCase(saveInvoiceNotes.fulfilled, (state) => {
            state.savingInvoiceNotes = false;
        })
            .addCase(saveInvoiceNotes.rejected, (state) => {
            state.savingInvoiceNotes = false;
        })
            // `fetchMappableInvoices` holds no flag either — the list belongs to whichever
            // rows are checked, so the modal tracks its own load.
            .addCase(saveInvoiceMappings.pending, (state) => {
            state.savingInvoiceMappings = true;
        })
            .addCase(saveInvoiceMappings.fulfilled, (state) => {
            state.savingInvoiceMappings = false;
        })
            .addCase(saveInvoiceMappings.rejected, (state) => {
            state.savingInvoiceMappings = false;
        });
        // `refreshInvoices` / `fetchRefreshStatus` carry no flags of their own: one
        // "refreshing" state has to span the kick-off AND the polling that follows it, so
        // the page owns it — see `handleRefresh`.
    },
});
// ✅ Selectors
export const selectBillingSummary = (state) => state.billingSummary.summary;
export const selectBillingSummaryLoading = (state) => state.billingSummary.loading;
export const selectBillingSummaryError = (state) => state.billingSummary.error;
export const selectSavingDevelopmentHours = (state) => state.billingSummary.savingDevelopmentHours;
export const selectSavingCommissionAmount = (state) => state.billingSummary.savingCommissionAmount;
export const selectSavingAdjustedAmounts = (state) => state.billingSummary.savingAdjustedAmounts;
export const selectGeneratingInvoice = (state) => state.billingSummary.generatingInvoice;
export const selectSavingInvoiceNotes = (state) => state.billingSummary.savingInvoiceNotes;
export const selectSavingInvoiceMappings = (state) => state.billingSummary.savingInvoiceMappings;
export default billingSummarySlice.reducer;
